import { useMutation, useQueryClient } from "@tanstack/vue-query";
import {
	useCreateBookingMutation,
	useCreateWalkInBookingMutation,
	useEndWalkInBookingMutation,
	useUpdateBookingMutation,
	useUpdateSeriesMutation,
	useDeleteBookingMutation,
	useDeleteSeriesMutation,
} from "../api";
import { queryKeys } from "../api/queryKeys.js";
import { useNotificationService } from "../calendar/composables/useNotificationService";
import { extractRoomId, extractSeriesId } from "./cacheStrategy.js";

/**
 * Booking Manager Service
 * Handles all booking CRUD operations and business logic
 * Extracted from BookingCalendar.vue for reuse across components
 */
export class BookingManager {
	constructor(queryClient, conflictService, budgetService = null, complianceService = null) {
		this.queryClient = queryClient;
		this.conflictService = conflictService;
		this.budgetService = budgetService;
		this.complianceService = complianceService;
		this.notifications = useNotificationService();

		// Initialize mutations
		this.createBookingMutation = useMutation(
			useCreateBookingMutation(queryClient)
		);
		this.createWalkInBookingMutation = useMutation(
			useCreateWalkInBookingMutation(queryClient)
		);
		this.endWalkInBookingMutation = useMutation(
			useEndWalkInBookingMutation(queryClient)
		);
		this.updateBookingMutation = useMutation(
			useUpdateBookingMutation(queryClient)
		);
		this.updateSeriesMutation = useMutation(
			useUpdateSeriesMutation(queryClient)
		);
		this.deleteBookingMutation = useMutation(
			useDeleteBookingMutation(queryClient)
		);
		this.deleteSeriesMutation = useMutation(
			useDeleteSeriesMutation(queryClient)
		);
	}

	// Loading states
	get isSaving() {
		return (
			this.createBookingMutation.isPending.value ||
			this.createWalkInBookingMutation.isPending.value ||
			this.endWalkInBookingMutation.isPending.value ||
			this.updateBookingMutation.isPending.value ||
			this.updateSeriesMutation.isPending.value
		);
	}

	get isDeleting() {
		return (
			this.deleteBookingMutation.isPending.value ||
			this.deleteSeriesMutation.isPending.value
		);
	}

	// Main booking action handler
	async executeBookingAction(action) {
		try {
			const result = await this._executeAction(action);
			this._handleActionSuccess(action, result);
			return result;
		} catch (error) {
			this._handleActionError(action, error);
			throw error;
		}
	}

	// Execute the appropriate booking action
	async _executeAction(action) {
		const { type, data } = action;

		switch (type) {
			case "create":
				return await this._handleCreateAction(data);
			case "create-walkin":
				return await new Promise((resolve, reject) => {
					this.createWalkInBookingMutation.mutate(data, {
						onSuccess: resolve,
						onError: reject,
					});
				});
			case "end-walkin":
				return await new Promise((resolve, reject) => {
					this.endWalkInBookingMutation.mutate(data, {
						onSuccess: resolve,
						onError: reject,
					});
				});
			case "update":
				return await new Promise((resolve, reject) => {
					const roomId = extractRoomId(data);
					this.updateBookingMutation.mutate(
						{
							bookingId: data.bookingId,
							bookingData: data.bookingData,
							roomId,
						},
						{
							onSuccess: resolve,
							onError: reject,
						}
					);
				});
			case "update-series":
				return await new Promise((resolve, reject) => {
					const roomId = extractRoomId(data);
					this.updateSeriesMutation.mutate(
						{ seriesId: data.seriesId, bookingData: data.bookingData, roomId },
						{
							onSuccess: resolve,
							onError: reject,
						}
					);
				});
			case "update-instance":
				return await new Promise((resolve, reject) => {
					const roomId = extractRoomId(data);
					const seriesId = extractSeriesId(data);
					// seriesId travels as part of the mutation variables (not the per-call
					// options object) so it reaches useUpdateBookingMutation's onSuccess,
					// where cache invalidation actually happens. TanStack Query's mutate()
					// options only support onSuccess/onError/onSettled — there is no
					// "context" option there (that name is reserved for onMutate's return
					// value), so passing it that way is silently a no-op.
					this.updateBookingMutation.mutate(
						{
							bookingId: data.bookingId,
							bookingData: data.bookingData,
							roomId,
							seriesId,
						},
						{
							onSuccess: resolve,
							onError: reject,
						}
					);
				});
			case "delete":
				return await new Promise((resolve, reject) => {
					// Try to extract context information for better cache invalidation
					const context = {
						roomId: data.roomId || data.booking?.roomId,
						seriesId: extractSeriesId(data),
					};
					this.deleteBookingMutation.mutate(data.bookingId, {
						onSuccess: resolve,
						onError: reject,
						context,
					});
				});
			case "delete-series":
				return await new Promise((resolve, reject) => {
					const context = {
						roomId: data.roomId || data.series?.roomId,
					};
					this.deleteSeriesMutation.mutate(data.seriesId, {
						onSuccess: resolve,
						onError: reject,
						context,
					});
				});
			case "delete-instance":
				return await new Promise((resolve, reject) => {
					const context = {
						roomId: data.roomId || data.booking?.roomId,
						seriesId: extractSeriesId(data),
					};
					this.deleteBookingMutation.mutate(data.bookingId, {
						onSuccess: resolve,
						onError: reject,
						context,
					});
				});
			default:
				throw new Error(`Unknown action type: ${type}`);
		}
	}

	// Handle create action with endpoint selection
	async _handleCreateAction(data) {
		const bookingData = data.endpoint ? data.data : data;
		const endpoint = data.endpoint || "instance/create";
		const roomId = extractRoomId({ bookingData, data });

		return await new Promise((resolve, reject) => {
			this.createBookingMutation.mutate(
				{ bookingData, roomId, endpoint },
				{
					onSuccess: resolve,
					onError: reject,
				}
			);
		});
	}

	// Handle successful actions
	_handleActionSuccess(action, result) {
		const { type, data } = action;

		switch (type) {
			case "create":
				const title =
					result?.data?.title ||
					data?.data?.title ||
					data?.title ||
					"Neue Buchung";
				this.notifications.notifyBookingSuccess("create", title);
				break;
			case "create-walkin":
				this.notifications.notifyBookingSuccess("create-walkin", "Sofortbuchung");
				break;
			case "end-walkin":
				this.notifications.notifyBookingSuccess("end-walkin", "Sofortbuchung");
				break;
			case "update":
			case "update-instance":
				const updateTitle =
					result?.title || data?.bookingData?.title || "Buchung";
				this.notifications.notifyBookingSuccess("update", updateTitle);
				break;
			case "update-series":
				const seriesTitle =
					result?.title || data?.bookingData?.title || "Serie";
				this.notifications.notifySeriesSuccess("update", seriesTitle);
				break;
			case "delete":
			case "delete-instance":
				this.notifications.notifyBookingSuccess("delete");
				break;
			case "delete-series":
				this.notifications.notifySeriesSuccess("delete");
				break;
		}

		// Refresh calendar events if available
		this._refreshCalendar?.();

		this.queryClient.invalidateQueries({ queryKey: queryKeys.account(), refetchType: "all" });
	}

	// Handle action errors
	_handleActionError(action, error) {
		const { type, data } = action;

		// Handle validation errors for create and update actions
		if (
			(type === "create" || type === "update" || type.includes("update")) &&
			error.isValidation
		) {
			this.notifications.notifyValidationError(error);
			return;
		}

		// Handle conflicts specially for create and update actions
		if (
			(type === "create" || type === "update" || type.includes("update")) &&
			error.isConflict
		) {
			// Enhance error object with original data for conflict resolution
			let originalData;

			if (type === "create") {
				originalData = data?.data || data;
			} else if (type === "update-series") {
				originalData = {
					seriesId: data?.seriesId,
					...data?.bookingData,
				};
			} else {
				originalData = {
					...(data?.bookingData || data),
				};
			}

			const enhancedError = {
				...error,
				originalData,
				originalMode: this._getOriginalModeFromType(type),
			};

			this.conflictService?.setConflict(enhancedError);
			return; // Don't show error toast for conflicts
		}

		// Handle budget errors specially for create and update actions
		if (
			(type === "create" || type === "update" || type.includes("update")) &&
			error.budget &&
			error.budgetDetails
		) {
			// Enhance error object with original data for budget error resolution
			let originalData;

			if (type === "create") {
				originalData = data?.data || data;
			} else if (type === "update-series") {
				originalData = {
					seriesId: data?.seriesId,
					...data?.bookingData,
				};
			} else {
				originalData = {
					...(data?.bookingData || data),
				};
			}

			const enhancedError = {
				...error,
				originalData,
				originalMode: this._getOriginalModeFromType(type),
			};

			// Set budget error in budget service if available
			if (this.budgetService) {
				this.budgetService.setBudgetError(enhancedError);
			}
			return; // Don't show error toast for budget errors
		}

		// Handle compliance errors specially for create and update actions
		if (
			(type === "create" || type === "update" || type.includes("update")) &&
			error.isCompliance
		) {
			// Enhance error object with original data for compliance error resolution
			let originalData;

			if (type === "create") {
				originalData = data?.data || data;
			} else if (type === "update-series") {
				originalData = {
					seriesId: data?.seriesId,
					...data?.bookingData,
				};
			} else {
				originalData = {
					...(data?.bookingData || data),
				};
			}

			const enhancedError = {
				...error,
				originalData,
				originalMode: this._getOriginalModeFromType(type),
			};

			// Set compliance error in compliance service if available
			if (this.complianceService) {
				this.complianceService.setComplianceViolation(enhancedError);
			}
			return; // Don't show error toast for compliance errors
		}

		// Show appropriate error notification
		if (type.includes("series")) {
			const operation = type.replace("-series", "").replace("-instance", "");
			this.notifications.notifySeriesError(operation, error);
		} else {
			const operation = type.replace("-instance", "");
			this.notifications.notifyBookingError(operation, error);
		}

		console.error(`${type} action failed:`, error);
	}

	// Helper to get original mode from action type
	_getOriginalModeFromType(type) {
		switch (type) {
			case "create":
				return "create";
			case "update-series":
				return "edit-series";
			case "update-instance":
				return "edit-instance";
			case "update":
				return "edit";
			default:
				return "create";
		}
	}

	// Handle conflict resolution
	async handleConflictResolution(resolutionData, originalData, originalMode) {
		try {
			// Determine the appropriate action based on original mode
			let action;

			if (originalMode === "create") {
				action = { type: "create", data: resolutionData };
			} else if (originalMode === "edit-series") {
				const seriesId = originalData.seriesId;
				if (!seriesId) {
					throw new Error("Series ID not found for update operation");
				}
				action = {
					type: "update-series",
					data: { seriesId, bookingData: resolutionData.data },
				};
			} else if (originalMode === "edit-instance" || originalMode === "edit") {
				const bookingId = originalData.bookingId;
				if (!bookingId) {
					throw new Error("Booking ID not found for update operation");
				}
				action = {
					type: "update-instance",
					data: { bookingId, bookingData: resolutionData.data },
				};
			} else {
				throw new Error(`Unknown original mode: ${originalMode}`);
			}

			// Execute the action using the same unified handler
			const result = await this._executeAction(action);
			this._handleActionSuccess(action, result);

			this.conflictService?.clearConflict();
			return result;
		} catch (error) {
			console.error("Conflict resolution failed:", error);

			// Handle error using the same unified error handler
			let actionType = "create";

			if (originalMode === "edit-series") {
				actionType = "update-series";
			} else if (originalMode === "edit-instance" || originalMode === "edit") {
				actionType = "update-instance";
			}

			this._handleActionError({ type: actionType, data: originalData }, error);
			throw error;
		}
	}

	// Set calendar refresh callback (for calendar component)
	setCalendarRefreshCallback(refreshCallback) {
		this._refreshCalendar = refreshCallback;
	}
}

/**
 * Composable for using BookingManager
 * Provides easy access to booking operations
 */
export function useBookingManager(conflictService, budgetService = null, complianceService = null) {
	const queryClient = useQueryClient();
	const bookingManager = new BookingManager(
		queryClient,
		conflictService,
		budgetService,
		complianceService
	);

	return {
		bookingManager,
		isSaving: bookingManager.isSaving,
		isDeleting: bookingManager.isDeleting,
		executeBookingAction: (action) =>
			bookingManager.executeBookingAction(action),
		handleConflictResolution: (resolutionData, originalData, originalMode) =>
			bookingManager.handleConflictResolution(
				resolutionData,
				originalData,
				originalMode
			),
		setCalendarRefreshCallback: (callback) =>
			bookingManager.setCalendarRefreshCallback(callback),
	};
}
